import React, { useEffect, useState } from 'react';

import {
  Alert,
  Skeleton, Stack, Typography,
} from '@mui/material';

import Button from '../../../../../components/Button';
import ContentCard from '../../../../../components/ContentCard';
import DetailsModal from '../../../../../components/DetailsModal';
import { useUpdateStreamMutation } from '../../../../../services/streams';
import { Stream } from '../../../../../services/streams/types';
import { SlackChannelContainer } from '../../../index.styles';

const INITIAL_SLACK_FORM = {
  label: '',
  value: '',
};

interface SlackChannelsCardProps {
  stream?: Stream
}

const SlackChannelsCard = ({ stream } : SlackChannelsCardProps) => {
  const [updateStream,
    { isLoading: isStreamUpdateLoading,
      isSuccess: isStreamUpdateSuccess,
      error: streamUpdateError,
      reset: resetStreamUpdate }] = useUpdateStreamMutation();

  const [slackFormOpen, setSlackFormOpen] = useState(false);
  const [slackForm, setSlackForm] = useState(INITIAL_SLACK_FORM);

  const [targetSlack, setTargetSlack] = useState<string | undefined>(undefined);

  const resetSlackModal = async () => {
    await resetStreamUpdate();
    setSlackForm(INITIAL_SLACK_FORM);
  };

  // Close modal after Slack channel is added
  useEffect(() => {
    if (isStreamUpdateSuccess) {
      setSlackFormOpen(false);
      resetSlackModal();
    }
  }, [isStreamUpdateSuccess]);

  if (!stream) {
    return <Skeleton variant="rectangular" height={128} />;
  }

  return (
    <>
      <ContentCard variant="outlined" title="Slack Channels">
        <Stack direction="column" spacing={2}>
          <Alert severity="warning">
            Any changes to Slack channels are applied to all students
            currently enrolled in the stream.
          </Alert>
          <Stack direction="column" spacing={1}>
            {(stream?.slackIds || []).map((slackId) => (
              <SlackChannelContainer onClick={() => window.open(`http://cognitotuition.slack.com/channels/${slackId.value}`, '_blank')}>
                <Stack key={slackId.value} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="column">
                    <Typography variant="subtitle2">
                      {slackId.label}
                    </Typography>
                    <Typography variant="caption">
                      {slackId.value}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    allCaps
                    disabled={isStreamUpdateLoading}
                    loading={isStreamUpdateLoading && targetSlack === slackId.value}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      setTargetSlack(slackId.value);
                      updateStream({
                        course: stream.course,
                        streamId: stream.streamId,
                        newAttributes: {
                          slackIds: stream?.slackIds
                            .filter((d) => d.value !== slackId.value),
                        },
                      });
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </SlackChannelContainer>
            ))}
          </Stack>
          <div>
            <Button
              size="small"
              color="darkPrimary"
              variant="outlined"
              allCaps
              onClick={() => setSlackFormOpen(true)}
            >
              Add Channel
            </Button>
          </div>
        </Stack>
      </ContentCard>
      <DetailsModal
        open={slackFormOpen}
        handleClose={() => {
          resetSlackModal();
          setSlackFormOpen(false);
        }}
        title="Add Slack Channel"
        fields={{
          label: {
            editable: true,
            label: 'Channel Name',
            type: 'text',
          },
          value: {
            editable: true,
            label: 'Channel ID',
            type: 'text',
          },
        }}
        error={streamUpdateError}
        values={slackForm}
        onFieldChange={(key, newValue) => setSlackForm({
          ...slackForm,
          [key]: newValue,
        })}
        buttons={[
          {
            label: 'Add',
            fn: async () => updateStream({
              course: stream.course,
              streamId: stream.streamId,
              newAttributes: {
                slackIds: [...(stream?.slackIds || []), slackForm],
              },
            }),
            loading: isStreamUpdateLoading,
          },
        ]}
      />
    </>
  );
};

export default SlackChannelsCard;
